/**
 * ZoomAuth.js — Zoom OAuth redirect handler for the AdminPortal web flow.
 *
 * Handles the web-browser Zoom OAuth flow:
 *   1. initiateZoomLogin() — redirects to the backend, which mints and binds state
 *   2. handleZoomCallback(state, onSuccess) — validates state, fetches JWT from backend,
 *      calls onSuccess(jwtToken, user)
 *
 * Auth flow:
 *   User click → initiateZoomLogin() → backend /oauth/zoom/authorize → zoom.us/oauth/authorize
 *   → Zoom login → backend /oauth/zoom/callback → backend stores JWT keyed by state
 *   → backend redirects to web UI with ?zoom_state={state}
 *   → handleZoomCallback(state, onSuccess) → GET /oauth/zoom/token?state={state} → JWT
 */

/**
 * Initiates the Zoom OAuth login flow for the web browser.
 *
 * Redirects the browser to the backend's /oauth/zoom/authorize endpoint.
 * The backend handles PKCE code_challenge construction and Zoom redirect.
 *
 * @returns {void}
 */
export function initiateZoomLogin() {
    // Build the backend authorize URL — backend handles the rest of the OAuth flow
    const apiBase = getApiBase();
    const clientRedirectUri = encodeURIComponent(`${window.location.origin}/login`);
    const authorizeUrl = `${apiBase}/oauth/zoom/authorize?client_redirect_uri=${clientRedirectUri}`;

    window.location.assign(authorizeUrl);
}

/**
 * Handles the Zoom OAuth callback after the backend redirects back to the web UI.
 *
 * Redeems the server-returned one-time state via GET /oauth/zoom/token?state={state},
 * then calls onSuccess(jwtToken, user).
 *
 * @param {string} state - The state parameter returned from the backend redirect
 * @param {function} onSuccess - Callback invoked with (jwtToken, user) on success
 * @returns {Promise<void>}
 * @throws {Error} When state is invalid/missing, or backend returns an error
 */
export async function handleZoomCallback(state, onSuccess) {
    if (!state) throw new Error('Zoom OAuth completion code is missing.');

    const apiBase = getApiBase();
    const tokenUrl = `${apiBase}/oauth/zoom/token?state=${encodeURIComponent(state)}`;

    const response = await fetch(tokenUrl, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Zoom OAuth token retrieval failed (${response.status}): ${errorText}`);
    }

    const data = await response.json();

    if (onSuccess) {
        onSuccess(data.JwtToken, data.User);
    }
}

/**
 * Returns the API base URL for the Zoom OAuth endpoints.
 * The /oauth/zoom/* endpoints live on the API project (zoomapi-*.meetingsync.ai),
 * NOT the Functions project (func-*.meetingsync.ai).
 *
 * @returns {string} Base URL, e.g. "https://zoomapi-test.qolsync.ai"
 */
function getApiBase() {
    return process.env.REACT_APP_ZOOM_API_URL || 'https://zoomapi-test.qolsync.ai';
}
